import { isWebPageSocketMessage, type WebPageSocketMessage } from '~/classes/desktopAppWebsocket/messagesTypes';

const DESKTOP_APP_WEBSOCKET_URL = 'ws://localhost:51182';
const DEFAULT_CONNECTION_TIMEOUT_MS = 2_000;

export type DesktopAppWebSocketSubscription = {
  onConnected: () => void;
  onDisconnected: () => void;
  onMessage: (message: WebPageSocketMessage) => void;
};

type ConnectOptions = {
  timeout?: number;
};

class _DesktopAppWebSocket {
  private connection: WebSocket | null = null;
  private isConnected = false;
  private subscription: DesktopAppWebSocketSubscription | null = null;
  private connectPromise: Promise<boolean> | null = null;
  private connectResolve: ((connected: boolean) => void) | null = null;
  private connectTimeoutId: number | null = null;

  connect = (callback: DesktopAppWebSocketSubscription, options?: ConnectOptions): Promise<boolean> => {
    this.subscription = callback;

    if (this.isConnected) {
      return Promise.resolve(true);
    }

    if (this.connectPromise) {
      return this.connectPromise;
    }

    const timeout = options?.timeout ?? DEFAULT_CONNECTION_TIMEOUT_MS;

    this.connectPromise = new Promise<boolean>((resolve) => {
      this.connectResolve = resolve;

      const finish = (connected: boolean) => {
        if (this.connectTimeoutId !== null) {
          window.clearTimeout(this.connectTimeoutId);
          this.connectTimeoutId = null;
        }

        this.connectResolve = null;
        this.connectPromise = null;

        if (!connected && this.connection) {
          this.connection.onopen = null;
          this.connection.onclose = null;
          this.connection.onerror = null;
          this.connection.onmessage = null;
          this.connection.close();
          this.connection = null;
        }

        resolve(connected);
      };

      this.connection = new WebSocket(DESKTOP_APP_WEBSOCKET_URL);

      this.connectTimeoutId = window.setTimeout(() => finish(false), timeout);

      this.connection.onopen = () => {
        if (!this.connectResolve) {
          return;
        }

        this.isConnected = true;
        this.subscription?.onConnected();
        finish(true);
      };

      this.connection.onmessage = (message) => {
        const event = JSON.parse(message.data.toString());

        if (isWebPageSocketMessage(event)) {
          this.subscription?.onMessage(event);
        }
      };

      this.connection.onclose = () => {
        this.isConnected = false;
        this.subscription?.onDisconnected();

        if (this.connectResolve) {
          finish(false);
        }
      };

      this.connection.onerror = () => {
        this.isConnected = false;
        this.subscription?.onDisconnected();

        if (this.connectResolve) {
          finish(false);
        }
      };
    });

    return this.connectPromise;
  };

  sendMessage = (message: WebPageSocketMessage) => {
    this.connection?.send(JSON.stringify(message));
  };
}

export const DesktopAppWebSocket = new _DesktopAppWebSocket();
